import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { Session } from 'next-auth';

interface AuthState {
  // Session data
  session: Session | null;
  status: 'authenticated' | 'loading' | 'unauthenticated';
  authUrl: string;
  // Self-hosted provider resources, keyed by provider id. Written by
  // useAuthInfo from a React Query fetch, so it is deliberately NOT persisted:
  // it is server-derived and refetched, and `authUrl`/`actualProvider` (the
  // values derived from it) are what actually need to survive a reload.
  providers: Record<string, any>;
  actualProvider: Record<string, any> | null;

  // Session actions
  setSession: (session: Session | null) => void;
  setStatus: (status: 'authenticated' | 'loading' | 'unauthenticated') => void;
  setAuthUrl: (authUrl: string) => void;
  setActualProvider: (provider: Record<string, any> | null) => void;
  getAuthUrl: (provider?: string) => string;

  // Manual state clean (optional, for edge cases)
  clearAllAuth: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      session: null,
      status: 'loading',
      authUrl: '',
      providers: {},
      actualProvider: null,

      // Session actions
      setSession: (session) => set({ session }),
      setStatus: (status) => set({ status }),
      setAuthUrl: (authUrl) => set({ authUrl }),
      setActualProvider: (actualProvider) => set({ actualProvider }),

      getAuthUrl: (provider?: string) => {
        const state = get();
        if (!provider) return state.authUrl;
        const providerData = state.providers[provider];
        if (providerData?.baseUrl) {
          return providerData.baseUrl;
        }
        // Well-known defaults for standard providers so that consumers (e.g.
        // observability-audit pages) always send a non-empty repo_host to the
        // backend.
        if (provider === 'github') return 'github.com';
        if (provider === 'gitlab') return 'gitlab.com';
        return state.authUrl;
      },

      // Manual: clear all auth state
      clearAllAuth: () => {
        set({
          session: null,
          status: 'unauthenticated',
          authUrl: '',
          providers: {},
          actualProvider: null,
        });
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      // Bumped to 1 so `migrate` runs once per existing browser. Zustand only
      // rewrites storage when a migration ran (its rehydrate path calls the
      // unwrapped `set`), so without the bump the fields dropped below would sit
      // in localStorage until some unrelated action happened to trigger a write.
      version: 1,
      migrate: (persisted) => {
        if (!persisted || typeof persisted !== 'object') return persisted;
        // v0 persisted the backend JWT plus identifiers derived from it
        // (userUUID/organizationUUID/expiresAt) and a `providers` map that never
        // had a writer. The credential now lives in an httpOnly cookie and the
        // rest is unused, so drop all of it instead of carrying it forward.
        const {
          jwt: _jwt,
          userUUID: _userUUID,
          organizationUUID: _organizationUUID,
          expiresAt: _expiresAt,
          providers: _providers,
          ...rest
        } = persisted as Record<string, unknown>;
        return rest;
      },
      partialize: (state) => ({
        authUrl: state.authUrl,
        actualProvider: state.actualProvider,
      }),
    }
  )
);
